import { HISTORY_DAYS } from "../engine";
import { previewable } from "../docs";
import { embedFor } from "../embeds";
import { useFlip } from "./flip";
import { StickerInfo } from "./Stickers";
import { isPhone } from "../history";
import { nativePlatform } from "../platform";
import { useGestures } from "./gestures";
import { useEffect, useRef, useState } from "preact/hooks";
import { Status, type Msg } from "../db";
import { fileExt, formatDuration, formatSize } from "../media";
import * as S from "../state";
import { timeOf } from "../util";
import { deleting, editing, forwarding, pinning, replyAfterOpen, replyTo, viewer } from "./chatui";
import { MessageInfo } from "./Groups";
import { ContactContent, LocationContent } from "./Share";
import { EventContent, PollContent } from "./PollEvent";
import { StatusMention } from "./Updates";
import { formatText } from "./format";
import { languageName, translate } from "../translate";
import { downloadMedia, useMediaSrc } from "./mediaSrc";
import { colorFor } from "../util";
import { EmojiPicker, QUICK_REACTIONS } from "./Emoji";
import { Icon, Ticks } from "./Icon";
import { engine, toast } from "./Main";

const URL_RE = /\bhttps?:\/\/[^\s<]+[^\s<.,:;"')\]!?]/g;

export function linkify(text: string) {
  const out: (string | preact.JSX.Element)[] = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    out.push(text.slice(last, m.index));
    out.push(
      <a href={m[0]} target="_blank" rel="noopener noreferrer">
        {m[0]}
      </a>,
    );
    last = m.index! + m[0].length;
  }
  out.push(text.slice(last));
  return out;
}

export function firstURL(text: string): string | undefined {
  return text.match(URL_RE)?.[0];
}

/** Translate on this device (the browser's built-in model). */
async function translateMsg(m: Msg) {
  let told = false;
  try {
    const t = await translate(m.body, (p) => p < 100 && !told && ((told = true), toast("Getting the translation model…")));
    await engine.value?.setTranslation(m, t);
  } catch (err) {
    toast((err as Error).message);
  }
}

/** Body text with WhatsApp-style formatting, @mentions highlighted and
 * links clickable. */
function richText(m: Msg) {
  return formatted(m.body, m.mentions ?? []);
}

export function formatted(body: string, mentions: { jid: string; begin: number; end: number }[] = []) {
  const ms = [...mentions].sort((a, b) => a.begin - b.begin).filter((x) => x.begin >= 0 && x.end <= body.length);
  const links = [...body.matchAll(URL_RE)].map((u) => [u.index!, u.index! + u[0].length] as [number, number]);
  // A plain stretch: mentions as chips, the rest with links.
  const leaf = (a: number, b: number) => {
    const out: (string | preact.JSX.Element)[] = [];
    let pos = a;
    for (const x of ms) {
      if (x.end <= pos || x.begin >= b) continue;
      const from = Math.max(x.begin, pos);
      const to = Math.min(x.end, b);
      out.push(...linkify(body.slice(pos, from)));
      const me = x.jid === engine.value?.me;
      out.push(
        <span key={"@" + from} class={"mention" + (me ? " me" : "")} title={S.memberName(x.jid)}>
          {body.slice(from, to)}
        </span>,
      );
      pos = to;
    }
    out.push(...linkify(body.slice(pos, b)));
    return out;
  };
  return formatText(body, leaf, [...links, ...ms.map((x) => [x.begin, x.end] as [number, number])]);
}

const isEmojiOnly = (s: string) =>
  /^(\p{Extended_Pictographic}|\p{Emoji_Component}|\s){1,12}$/u.test(s) &&
  !/\d/.test(s);

export function Bubble({ m, tail }: { m: Msg; tail: boolean }) {
  const out = m.dir === "out";
  const [menu, setMenu] = useState(false);
  const [original, setOriginal] = useState(false);
  const [react, setReact] = useState<"none" | "quick" | "full">("none");
  // Opened by holding a finger on the message (phone): the bar sits above it.
  const [held, setHeld] = useState<false | "above" | "below">(false);
  const hoverTimer = useRef<number | undefined>(undefined);
  // Not enough room above the message (top of the list): open downwards.
  const [below, setBelow] = useState(false);
  const roomAbove = () => {
    const top = ref.current?.getBoundingClientRect().top ?? 0;
    const list = ref.current?.closest(".messages")?.getBoundingClientRect().top ?? 0;
    return top - list >= 64;
  };
  const hoverOpened = useRef(false);
  const openQuick = (byHover: boolean) => (setBelow(!roomAbove()), setReact((r) => (r === "none" ? ((hoverOpened.current = byHover), "quick") : r)));
  const wrapRef = useRef<HTMLDivElement>(null);
  const [info, setInfo] = useState(false);
  const group = S.isGroup(m.chat);
  const showAuthor = group && !out && tail;
  const starred = S.stars.value.has(m.key);
  const focus = S.focusMessage.value === m.key;
  const ref = useRef<HTMLDivElement>(null);
  // Gestures: swipe right = reply, swipe left = delete, long-press /
  // right-click = message menu, double-click = reply (desktop).
  const canReply =
    !m.deleted &&
    !m.undecryptable &&
    !S.isChannel(m.chat) &&
    (!S.isGroup(m.chat) || S.canPost(m.chat).ok);
  const reply = () => {
    editing.value = null;
    replyTo.value = m;
    document.querySelector<HTMLTextAreaElement>(".composer textarea")?.focus();
  };
  const g = useGestures({
    onSwipeRight: canReply ? reply : undefined,
    onSwipeLeft: () => (deleting.value = m),
    onLongPress:
      m.deleted || m.undecryptable
        ? undefined
        : (touch) => {
            setMenu(true);
            // Hold to react (phone): the quick reactions come with the menu.
            if (touch && reactable) {
              setHeld(roomAbove() ? "above" : "below");
              setReact("quick");
            }
          },
  });
  const reactable = !m.deleted && !m.undecryptable && m.kind !== "system";
  // Hover to react (mouse): rest on a message and its quick reactions appear.
  // (Pointer events: a finger's tap also sends mouse events, which mustn't count.)
  const hoverIn = (e: PointerEvent) => {
    if (e.pointerType !== "mouse" || !reactable) return;
    clearTimeout(hoverTimer.current);
    if (react === "none" && !menu) hoverTimer.current = window.setTimeout(() => openQuick(true), HOVER_MS);
  };
  const hoverOut = (e: PointerEvent) => {
    clearTimeout(hoverTimer.current);
    if (e.pointerType === "mouse" && react === "quick" && !held) hoverTimer.current = window.setTimeout(() => setReact((r) => (r === "quick" ? "none" : r)), 300);
  };
  // (Reacting, or closing the bar, closes the message menu too.)
  const closeReact = () => ((hoverOpened.current = false), setReact("none"), setMenu(false), setHeld(false));
  // Tap / click elsewhere or Escape closes them.
  useEffect(() => {
    if (react === "none") return;
    // (Not on the message menu: its own items close everything when used.)
    const onDown = (ev: PointerEvent) => !wrapRef.current?.contains(ev.target as Node) && !(ev.target as HTMLElement).closest?.(".quick-react, .react-picker, .msg-menu") && closeReact();
    const onKey = (ev: KeyboardEvent) => ev.key === "Escape" && closeReact();
    const t = setTimeout(() => document.addEventListener("pointerdown", onDown));
    window.addEventListener("keydown", onKey);
    return () => (clearTimeout(t), document.removeEventListener("pointerdown", onDown), window.removeEventListener("keydown", onKey));
  }, [react]);
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  useEffect(() => {
    if (!focus) return;
    ref.current?.scrollIntoView({ block: "center" });
    const t = setTimeout(() => (S.focusMessage.value = null), 2000);
    return () => clearTimeout(t);
  }, [focus]);

  if (m.undecryptable) {
    return (
      <div
        class={"row " + m.dir + (tail ? " tail" : "")}
        data-key={m.key}
        ref={ref}
      >
        <div class="bubble deleted undecryptable">
          <span class="text">
            <Icon name="lock" size={14} /> <UnreadableText m={m} />
          </span>
          <Meta m={m} starred={false} />
        </div>
      </div>
    );
  }

  if (m.deleted) {
    return (
      <div
        class={"row " + m.dir + (tail ? " tail" : "")}
        data-key={m.key}
        ref={ref}
      >
        <div class="bubble deleted">
          <span class="text">
            <Icon name="block" size={16} />{" "}
            {out ? "You deleted this message" : "This message was deleted"}
          </span>
          <Meta m={m} starred={false} />
        </div>
      </div>
    );
  }

  const media = m.kind !== "text" && !m.viewOnce && m.media;
  const card = m.statusRef ? (
    <StatusMention m={m} />
  ) : m.kind === "contact" ? (
      <ContactContent m={m} />
    ) : m.kind === "location" ? (
      <LocationContent m={m} />
    ) : m.kind === "poll" && m.poll ? (
      <PollContent m={m} />
    ) : m.kind === "event" && m.event ? (
      <EventContent m={m} />
    ) : null;
  const plain = m.kind === "text" && !m.replyTo && !m.preview && !m.forwarded;
  const emoji = plain && isEmojiOnly(m.body);
  const visual =
    m.kind === "image" || m.kind === "video" || m.kind === "location";

  return (
    <div
      class={
        "row swipeable " +
        m.dir +
        (tail ? " tail" : "") +
        (focus ? " focus" : "")
      }
      data-key={m.key}
      ref={ref}
      data-held={react === "quick" && held ? held : undefined}
      data-reacting={react !== "none" || menu ? "" : undefined}
      onPointerEnter={hoverIn}
      onPointerLeave={hoverOut}
      {...g.bind}
      onDblClick={(e) =>
        canReply &&
        !(e.target as HTMLElement).closest("button, a, img, video, audio") &&
        (window.getSelection()?.removeAllRanges(), reply())
      }
    >
      <span class="swipe-hint hint-right" aria-hidden="true">
        <Icon name="reply" size={20} />
      </span>
      <span class="swipe-hint hint-left" aria-hidden="true">
        <Icon name="trash" size={20} />
      </span>
      <div
        class="bubble-wrap swipe-move"
        ref={g.target as preact.Ref<HTMLDivElement>}
      >
        <div
          class={
            "bubble" +
            (emoji || m.kind === "sticker" ? " emoji" : "") +
            (m.kind === "sticker" ? " sticker" : "") +
            (m.media?.note ? " emoji note" : "") +
            (visual ? " visual" : "") +
            (m.kind === "voice" ? " voice" : "")
          }
        >
          <button
            class="bubble-menu-btn"
            aria-label="Message options"
            onClick={() => setMenu(!menu)}
          >
            <Icon name="down" size={20} />
          </button>
          {showAuthor && (
            <div class="author" style={{ color: colorFor(m.from) }}>
              {S.memberName(m.from)}
            </div>
          )}
          {m.forwarded && (
            <div class="fwd-label">
              <Icon name="forward" size={14} /> Forwarded
            </div>
          )}
          {m.replyTo && <QuoteBlock chat={m.chat} q={m.replyTo} />}
          {m.viewOnce && <ViewOnce m={m} />}
          {media && <MediaContent m={m} />}
          {card}
          {m.preview ? <PreviewCard p={m.preview} /> : <BareEmbed m={m} />}
          {m.body && !m.statusRef && <span class="text">{m.translation && !original ? formatted(m.translation.text) : richText(m)}</span>}
          {m.translation && m.body && (
            <span class="translated">
              <Icon name="translate" size={14} /> {original ? "Original" : `Translated from ${languageName(m.translation.from)}`} ·{" "}
              <button class="link" onClick={() => setOriginal(!original)}>
                {original ? "Show translation" : "Show original"}
              </button>
            </span>
          )}
          <Meta
            m={m}
            starred={starred}
            overlay={(m.kind === "image" || m.kind === "video") && !m.body}
          />
        </div>
        {m.reactions && Object.keys(m.reactions).length > 0 && (
          <Reactions m={m} />
        )}
        {m.chanReactions &&
          Object.values(m.chanReactions).some((n) => n > 0) && (
            <ChannelReactions m={m} />
          )}
        {react === "quick" && held && <QuickReact m={m} place={held} onPick={closeReact} onMore={() => (setHeld(false), setReact("full"))} />}
        {reactable && (
          <div class="react-btn-wrap" ref={wrapRef}>
            <button
              class="react-btn"
              aria-label="React"
              onPointerEnter={(e) => e.pointerType === "mouse" && (clearTimeout(hoverTimer.current), openQuick(true))}
              // (A click right after the hover opened it keeps it open.)
              onClick={() => (setHeld(false), react === "none" ? openQuick(false) : hoverOpened.current ? (hoverOpened.current = false) : setReact("none"))}
            >
              <Icon name="smiley" size={18} />
            </button>
            {react === "quick" && !held && <QuickReact m={m} below={below} onPick={closeReact} onMore={() => setReact("full")} />}
            {react === "full" && (
              <EmojiPicker
                class={"react-picker" + (below ? " below" : "")}
                onClose={closeReact}
                onPick={(e) => (closeReact(), void engine.value?.react(m, e))}
              />
            )}
          </div>
        )}
        {menu && (
          <MessageMenu
            m={m}
            starred={starred}
            onClose={() => (setMenu(false), held && closeReact())}
            onInfo={() => setInfo(true)}
          />
        )}
        {info && <MessageInfo m={m} onClose={() => setInfo(false)} />}
      </div>
    </div>
  );
}

const HOVER_MS = 600;

/** The quick reactions bar: one tap reacts (again on yours takes it back). */
function QuickReact({ m, place, below, onPick, onMore }: { m: Msg; place?: "above" | "below"; below?: boolean; onPick: () => void; onMore: () => void }) {
  const mine = m.reactions?.[engine.value?.me ?? ""];
  return (
    <div class={"quick-react" + (place ? " held " + place : below ? " below" : "")} role="menu" aria-label="React" onPointerDown={(e) => e.stopPropagation()}>
      {QUICK_REACTIONS.map((e) => (
        <button key={e} class={mine === e ? "on" : ""} aria-label={`React ${e}`} onClick={() => (onPick(), void engine.value?.react(m, mine === e ? null : e))}>
          {e}
        </button>
      ))}
      <button aria-label="More reactions" onClick={onMore}>
        <Icon name="plus" size={18} />
      </button>
    </div>
  );
}

/** View-once photo/video: one tap to open; afterwards it's gone. */
function ViewOnce({ m }: { m: Msg }) {
  const label = m.kind === "video" ? "Video" : "Photo";
  if (m.dir === "out") {
    return (
      <span class="view-once done">
        <span class="view-once-ring">1</span> {label}
      </span>
    );
  }
  if (m.viewed || !m.media?.url) {
    return (
      <span class="view-once done">
        <span class="view-once-ring">1</span> Opened
      </span>
    );
  }
  return (
    <button
      class="view-once"
      onClick={() => (viewer.value = m)}
      aria-label={`Open view-once ${label.toLowerCase()}`}
    >
      <span class="view-once-ring">1</span> {label}
    </button>
  );
}

function Meta({
  m,
  starred,
  overlay,
}: {
  m: Msg;
  starred: boolean;
  overlay?: boolean;
}) {
  return (
    <span class={"meta" + (overlay ? " overlay" : "")}>
      {m.insecure && m.dir === "in" && (
        <span
          class="insecure"
          title="This message was not end-to-end encrypted"
        >
          Not encrypted
        </span>
      )}
      {m.pin && m.pin.until > Date.now() && (
        <span class="meta-mark" title="Pinned">
          <Icon name="pin" size={12} />
        </span>
      )}
      {m.kept && (
        <span class="meta-mark" title="Kept in chat">
          <Icon name="star" size={12} />
          Kept
        </span>
      )}
      {starred && <Icon name="star" size={12} />}
      {m.editedAt && <span class="edited">Edited</span>}
      {timeOf(m.ts)}
      {m.dir === "out" &&
        (m.status === Status.Failed ? (
          <button
            class="retry"
            onClick={() => void engine.value?.retry(m.chat, m.id)}
            aria-label="Retry sending"
          >
            <Ticks status={m.status} />
          </button>
        ) : (
          <Ticks status={S.shownStatus(m.chat, m.status)} />
        ))}
    </span>
  );
}

function MessageMenu({
  m,
  starred,
  onClose,
  onInfo,
}: {
  m: Msg;
  starred: boolean;
  onClose: () => void;
  onInfo: () => void;
}) {
  const e = engine.value!;
  const ref = useRef<HTMLUListElement>(null);
  useEffect(() => {
    // Pointer events: the mouse events a finger's long-press ends with
    // (after the menu opened) mustn't close it again.
    const onDown = (ev: PointerEvent) =>
      ref.current && !ref.current.contains(ev.target as Node) && !(ev.target as HTMLElement).closest?.(".quick-react") && onClose();
    const t = setTimeout(() => document.addEventListener("pointerdown", onDown));
    return () => (clearTimeout(t), document.removeEventListener("pointerdown", onDown));
  }, []);
  const act = (fn: () => unknown) => () => {
    onClose();
    fn();
  };
  const flip = useFlip(ref, ".messages", 26);
  // Our own messages can be forwarded once they reached the server.
  const sendable =
    m.dir === "in" ||
    (m.status !== Status.Failed && m.status !== Status.Pending);
  return (
    <ul class={"menu msg-menu " + m.dir + (flip.up ? " up" : "")} ref={ref} role="menu" style={flip.maxHeight ? { maxHeight: `${flip.maxHeight}px`, overflowY: "auto" } : undefined}>
      {S.isGroup(m.chat) && m.dir === "out" && (
        <li role="menuitem" onClick={act(onInfo)}>
          Message info
        </li>
      )}
      {!S.isChannel(m.chat) && (!S.isGroup(m.chat) || S.canPost(m.chat).ok) && (
        <li
          role="menuitem"
          onClick={act(() => ((editing.value = null), (replyTo.value = m)))}
        >
          Reply
        </li>
      )}
      {S.isGroup(m.chat) && m.dir === "in" && m.kind !== "system" && (
        <li role="menuitem" onClick={act(() => ((replyAfterOpen.value = { chat: m.from, m }), void e.openChat(m.from)))}>
          Reply privately
        </li>
      )}
      {e.canPin(m.chat) && !m.deleted && (m.pin && m.pin.until > Date.now() ? (
        <li role="menuitem" onClick={act(() => void e.pinMessage(m, 0).then(() => toast("Message unpinned")))}>
          Unpin
        </li>
      ) : (
        <li role="menuitem" onClick={act(() => (pinning.value = m))}>
          Pin
        </li>
      ))}
      {!!m.ttl && !m.deleted && (
        <li role="menuitem" onClick={act(() => void e.keepMessage(m, !m.kept).then(() => toast(m.kept ? "Message no longer kept" : "Message kept in chat")))}>
          {m.kept ? "Unkeep" : "Keep in chat"}
        </li>
      )}
      {m.body && m.dir === "in" && !m.statusRef && (
        <li role="menuitem" onClick={act(() => (m.translation ? void e.setTranslation(m, null) : void translateMsg(m)))}>
          {m.translation ? "Remove translation" : "Translate"}
        </li>
      )}
      {m.body && (
        <li
          role="menuitem"
          onClick={act(() =>
            navigator.clipboard
              ?.writeText(m.body)
              .then(() => toast("Message copied")),
          )}
        >
          Copy
        </li>
      )}
      {sendable && !m.viewOnce && (
        <li role="menuitem" onClick={act(() => (forwarding.value = [m]))}>
          Forward
        </li>
      )}
      <li
        role="menuitem"
        onClick={act(() =>
          e.toggleStar(m).catch((err) => toast(String(err.message ?? err))),
        )}
      >
        {starred ? "Unstar" : "Star"}
      </li>
      {e.canEdit(m) && (
        <li
          role="menuitem"
          onClick={act(() => ((replyTo.value = null), (editing.value = m)))}
        >
          Edit
        </li>
      )}
      {m.media?.url && !m.viewOnce && (
        <li
          role="menuitem"
          onClick={act(() =>
            downloadMedia(m.media!).catch((err) =>
              toast(String(err.message ?? err)),
            ),
          )}
        >
          Download
        </li>
      )}
      <li
        role="menuitem"
        class="danger"
        onClick={act(() => (deleting.value = m))}
      >
        Delete
      </li>
    </ul>
  );
}

/** Channels: counts only (who reacted stays private). */
function ChannelReactions({ m }: { m: Msg }) {
  const counts = Object.entries(m.chanReactions ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
  const total = counts.reduce((t, [, n]) => t + n, 0);
  return (
    <div class="reactions" aria-label={`${total} reactions`}>
      {counts.slice(0, 4).map(([e]) => (
        <button
          key={e}
          class={m.myReaction === e ? "mine" : ""}
          title={m.myReaction === e ? "Your reaction — click to remove" : ""}
          onClick={() =>
            m.myReaction === e && void engine.value?.react(m, null)
          }
        >
          {e}
        </button>
      ))}
      <span class="reaction-total">{total}</span>
    </div>
  );
}

function Reactions({ m }: { m: Msg }) {
  const me = engine.value?.me ?? "";
  const counts = new Map<string, string[]>();
  for (const [jid, e] of Object.entries(m.reactions ?? {}))
    counts.set(e, [...(counts.get(e) ?? []), jid]);
  const who = (jids: string[]) =>
    jids.map((j) => (j === me ? "You" : S.displayName(j))).join(", ");
  return (
    <div class="reactions">
      {[...counts].map(([e, jids]) => (
        <button
          key={e}
          class={jids.includes(me) ? "mine" : ""}
          title={who(jids) + (jids.includes(me) ? " — click to remove" : "")}
          onClick={() => jids.includes(me) && void engine.value?.react(m, null)}
        >
          {e}
          {jids.length > 1 && <span>{jids.length}</span>}
        </button>
      ))}
    </div>
  );
}

function QuoteBlock({
  chat,
  q,
}: {
  chat: string;
  q: NonNullable<Msg["replyTo"]>;
}) {
  const me = engine.value?.me;
  const status = q.id.startsWith("status:");
  const author =
    (q.from === me ? "You" : S.memberName(q.from || chat)) +
    (status ? " · Status" : "") +
    (q.chat && q.chat !== chat ? ` · ${S.displayName(q.chat)}` : "");
  return (
    <button
      class={"quote" + (q.from === me ? " mine" : "")}
      onClick={() => {
        if (status) return toast("Status updates disappear after 24 hours");
        const at = q.chat ?? chat; // a private reply's quote is in its group
        const key = at + "\u0000" + q.id;
        if (document.querySelector(`[data-key="${CSS.escape(key)}"]`))
          S.focusMessage.value = key;
        else
          void engine.value
            ?.getMessage(key)
            .then((m) =>
              m
                ? engine.value?.openChat(at, m)
                : toast("Original message isn't loaded on this device"),
            );
      }}
    >
      <span class="quote-author">{author}</span>
      <span class="quote-text">{q.text ?? "Message"}</span>
    </button>
  );
}

const PROVIDER = { youtube: "YouTube", vimeo: "Vimeo", spotify: "Spotify", dailymotion: "Dailymotion", loom: "Loom" };

/** A playable link without a preview (the sender's preview failed or was
 * dismissed): still offer the player. */
function BareEmbed({ m }: { m: Msg }) {
  const url = m.kind === "text" && !m.deleted ? firstURL(m.body) : undefined;
  const embed = url && embedFor(url);
  if (!url || !embed) return null;
  return <PreviewCard p={{ url, title: `${PROVIDER[embed.provider]} ${embed.provider === "spotify" ? "music" : "video"}`, site_name: PROVIDER[embed.provider] }} />;
}

/** A link's preview. Wide images show as a big card; YouTube, Vimeo,
 * Spotify… play right here (the player loads only when tapped). */
function PreviewCard({ p }: { p: NonNullable<Msg["preview"]> }) {
  const embed = embedFor(p.url);
  const [playing, setPlaying] = useState(false);
  const big = !!embed || !!(p.thumb && p.large);
  const text = (
    <a class="preview-text" href={p.url} target="_blank" rel="noopener noreferrer">
      <strong>{p.title}</strong>
      {p.description && <p>{p.description}</p>}
      <small>{p.site_name ?? new URL(p.url).hostname}</small>
    </a>
  );
  return (
    <div class={"link-preview" + (big || playing ? " large" : "") + (embed ? " embed-" + embed.provider : "")}>
      {playing && embed ? (
        <div
          class="embed-frame"
          style={
            embed.height
              ? { height: `${embed.height}px` }
              : // Vertical videos (Shorts): narrower, not 600 px tall.
                { aspectRatio: String(embed.aspect ?? 16 / 9), ...((embed.aspect ?? 1) < 1 ? { width: "min(100%, 260px)", margin: "0 auto" } : {}) }
          }
        >
          <iframe
            src={embed.src}
            title={p.title}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write"
            allowFullScreen
            referrerpolicy="strict-origin-when-cross-origin"
          />
        </div>
      ) : embed ? (
        <button class="preview-media" onClick={() => setPlaying(true)} aria-label={`Play ${p.title}`}>
          {p.thumb ? <img src={p.thumb} alt="" /> : <span class="preview-noimg" />}
          <span class="play-badge" aria-hidden="true">
            <Icon name="play" size={30} />
          </span>
        </button>
      ) : (
        p.thumb && (
          <a class="preview-media" href={p.url} target="_blank" rel="noopener noreferrer" tabIndex={-1}>
            <img src={p.thumb} alt="" />
          </a>
        )
      )}
      {text}
    </div>
  );
}

// ---- media ----------------------------------------------------------------------

/** A voice message's transcript, made on this device (Whisper; nothing is sent). */
function Transcript({ m, src }: { m: Msg; src?: string }) {
  const [state, setState] = useState<string | null>(null);
  if (m.transcript !== undefined) return <p class="transcript">{m.transcript || <span class="muted">(no speech)</span>}</p>;
  async function run() {
    if (!src) return;
    setState("Getting ready…");
    try {
      const { transcribe } = await import("../transcribe");
      const text = await transcribe(await (await fetch(src)).blob(), (p, working) => setState(working ? "Transcribing…" : `Downloading the speech model ${p}%…`));
      await engine.value?.setTranscript(m, text);
      setState(null);
    } catch (e) {
      setState(null);
      toast("Couldn't transcribe: " + (e as Error).message);
    }
  }
  return state ? (
    <p class="transcript muted">{state}</p>
  ) : (
    <button class="link transcribe-btn" disabled={!src} onClick={() => void run()} title="Transcribe on this device">
      Transcribe
    </button>
  );
}

/** GIFs (looping, silent) and video notes (round; tap for sound) play right in the chat. */
function LoopVideo({ m, src, uploading, progress }: { m: Msg; src?: string; uploading: boolean; progress?: number }) {
  const v = useRef<HTMLVideoElement>(null);
  const [sound, setSound] = useState(false);
  const note = !!m.media!.note;
  const tap = () => {
    const el = v.current;
    if (!el) return;
    if (!note) return void (el.paused ? el.play() : el.pause());
    // A video note: tap plays it with sound from the start; tap again mutes.
    if (sound) {
      el.muted = true;
      setSound(false);
    } else {
      el.muted = false;
      el.currentTime = 0;
      void el.play();
      setSound(true);
    }
  };
  return (
    <div class={note ? "video-note" : "gif-msg"}>
      {src ? (
        <video ref={v} src={src} autoPlay loop muted playsInline onClick={tap} aria-label={note ? (sound ? "Mute video note" : "Play video note with sound") : "GIF"} />
      ) : (
        <span class="loop-wait" style={m.media!.thumb ? { backgroundImage: `url("${m.media!.thumb}")` } : undefined} />
      )}
      {!note && <span class="gif-badge">GIF</span>}
      {note && <span class="note-len">{formatDuration(m.media!.duration ?? 0)}</span>}
      {uploading && <UploadRing m={m} progress={progress} />}
    </div>
  );
}

function StickerContent({ m, src }: { m: Msg; src?: string }) {
  const [info, setInfo] = useState(false);
  return (
    <>
      <button class="sticker-msg" onClick={() => setInfo(true)} aria-label={m.sticker?.pack ? "Sticker: view its pack" : "Sticker"}>
        {src ? <img src={src} alt={m.sticker?.emoji ?? "Sticker"} /> : <span class="sticker-wait" />}
      </button>
      {info && <StickerInfo m={m} onClose={() => setInfo(false)} />}
    </>
  );
}

function MediaContent({ m }: { m: Msg }) {
  const media = m.media!;
  // Photos and voice notes decrypt as they appear; videos when opened in
  // the viewer; documents only when downloaded.
  const eager = m.kind === "image" || m.kind === "sticker" || m.kind === "voice" || m.kind === "audio" || !!media.gif || !!media.note;
  const { src, error } = useMediaSrc(media, eager);
  const openable = m.kind === "video" ? !!(media.url || media.blob) : !!src;
  const progress = S.uploads.value.get(m.key);
  const uploading = m.status === Status.Pending && !media.url;

  if (m.kind === "sticker") return <StickerContent m={m} src={src} />;
  if (m.kind === "video" && (media.gif || media.note)) return <LoopVideo m={m} src={src} uploading={uploading} progress={progress} />;

  if (m.kind === "image" || m.kind === "video") {
    const w = media.width ?? 320;
    const h = media.height ?? 240;
    const maxW = 330;
    const dispW = Math.min(maxW, w);
    const dispH = Math.min(Math.round((dispW * h) / w), 420);
    return (
      <div
        class="media-visual"
        style={{
          width: `${dispW}px`,
          height: `${dispH}px`,
          backgroundImage: media.thumb ? `url("${media.thumb}")` : undefined,
        }}
      >
        <button
          class="media-open"
          onClick={() => openable && (viewer.value = m)}
          aria-label={m.kind === "image" ? "Open photo" : "Play video"}
          title={error}
        >
          {m.kind === "image" && src && <LazyImg src={src} />}
          {m.kind === "video" && (
            <>
              <span class="play-badge">
                <Icon name="play" size={34} />
              </span>
              <span class="duration-badge">
                <Icon name="videocam" size={14} />{" "}
                {formatDuration(media.duration)}
              </span>
            </>
          )}
        </button>
        {uploading && <UploadRing m={m} progress={progress} />}
      </div>
    );
  }

  if (m.kind === "voice" || m.kind === "audio")
    return (
      <>
        <VoicePlayer m={m} src={src} uploading={uploading} progress={progress} />
        {m.kind === "voice" && <Transcript m={m} src={src} />}
      </>
    );

  // PDFs and text files open in the viewer; a PDF shows its first page.
  const canOpen =
    previewable(media.mime, media.name) && !!(media.url || media.blob);
  const open = () => canOpen && (viewer.value = m);
  return (
    <div class={"doc-wrap" + (media.thumb ? " has-preview" : "")}>
      {media.thumb && (
        <button
          class="doc-preview"
          onClick={open}
          aria-label={`Open ${media.name ?? "document"}`}
        >
          <img src={media.thumb} alt="" />
        </button>
      )}
      <div class="doc">
        <span class="doc-icon">{fileExt(media.name)}</span>
        <div
          class={"doc-info" + (canOpen ? " openable" : "")}
          onClick={open}
          role={canOpen ? "button" : undefined}
          aria-label={
            canOpen ? `Preview ${media.name ?? "document"}` : undefined
          }
        >
          <span class="doc-name">{media.name ?? "Document"}</span>
          <small>
            {fileExt(media.name)} ·{" "}
            {media.pages
              ? `${media.pages} page${media.pages > 1 ? "s" : ""} · `
              : ""}
            {formatSize(media.size)}
          </small>
        </div>
        {uploading ? (
          <UploadRing m={m} progress={progress} small />
        ) : (
          media.url && (
            <button
              class="icon-btn doc-download"
              onClick={() =>
                void downloadMedia(media).catch((e) =>
                  toast(String(e.message ?? e)),
                )
              }
              aria-label={`Download ${media.name}`}
            >
              <Icon name="download" />
            </button>
          )
        )}
      </div>
    </div>
  );
}

function LazyImg({ src }: { src: string }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={src}
      alt=""
      class={loaded ? "loaded" : ""}
      onLoad={() => setLoaded(true)}
      loading="lazy"
      decoding="async"
    />
  );
}

function UploadRing({
  m,
  progress,
  small,
}: {
  m: Msg;
  progress?: number;
  small?: boolean;
}) {
  const pct = Math.round((progress ?? 0) * 100);
  return (
    <button
      class={"upload-ring" + (small ? " small" : "")}
      onClick={() => engine.value?.cancelUpload(m.key)}
      aria-label={`Uploading ${pct}%, click to cancel`}
    >
      <svg viewBox="0 0 36 36" width={small ? 32 : 48} height={small ? 32 : 48}>
        <circle cx="18" cy="18" r="16" fill="rgba(0,0,0,.45)" />
        <circle
          cx="18"
          cy="18"
          r="14"
          fill="none"
          stroke="#fff"
          stroke-width="2.5"
          stroke-dasharray={`${(pct / 100) * 88} 88`}
          transform="rotate(-90 18 18)"
        />
        <path d="M13 13l10 10M23 13L13 23" stroke="#fff" stroke-width="2" />
      </svg>
    </button>
  );
}

function VoicePlayer({
  m,
  src,
  uploading,
  progress,
}: {
  m: Msg;
  src?: string;
  uploading: boolean;
  progress?: number;
}) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [rate, setRate] = useState(1);
  const dur = m.media?.duration ?? 0;
  const bars =
    m.media?.waveform ??
    Array.from({ length: 48 }, (_, i) => 30 + ((i * 37) % 50));
  const frac = dur ? Math.min(1, pos / dur) : 0;

  function toggle() {
    const a = audio.current;
    if (!a) return;
    if (a.paused) {
      // Only one voice note plays at a time.
      document.querySelectorAll("audio").forEach((x) => x !== a && x.pause());
      void a.play();
    } else a.pause();
  }

  function seek(e: MouseEvent) {
    const a = audio.current;
    const el = e.currentTarget as HTMLElement;
    if (!a || !dur) return;
    const r = el.getBoundingClientRect();
    a.currentTime = ((e.clientX - r.left) / r.width) * dur;
    setPos(a.currentTime);
  }

  return (
    <div class="voice-player">
      {src && (
        <audio
          ref={audio}
          src={src}
          preload="none"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => (setPlaying(false), setPos(0))}
          onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        />
      )}
      {uploading ? (
        <UploadRing m={m} progress={progress} small />
      ) : (
        <button
          class="icon-btn play"
          onClick={toggle}
          disabled={!src}
          aria-label={playing ? "Pause" : "Play"}
        >
          <Icon name={playing ? "pause" : "play"} size={28} />
        </button>
      )}
      <div class="voice-main">
        <div
          class="waveform"
          onClick={seek}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(dur)}
          aria-valuenow={Math.round(pos)}
          tabIndex={0}
        >
          {bars.map((b, i) => (
            <span
              key={i}
              class={i / bars.length < frac ? "played" : ""}
              style={{ height: `${Math.max(12, b)}%` }}
            />
          ))}
        </div>
        <small>{formatDuration(playing || pos ? pos : dur)}</small>
      </div>
      {m.kind === "voice" ? (
        <button
          class="rate"
          onClick={() => {
            const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
            setRate(next);
            if (audio.current) audio.current.playbackRate = next;
          }}
          aria-label="Playback speed"
        >
          {rate}×
        </button>
      ) : (
        <span class="doc-icon small">{fileExt(m.media?.name)}</span>
      )}
    </div>
  );
}

/** Why an end-to-end encrypted message isn't readable here, and what helps. */
function UnreadableText({ m }: { m: Msg }) {
  const st = S.historyState.value;
  const recent = Date.now() - m.ts < HISTORY_DAYS * 86_400_000;
  if (st === "waiting" || st === "importing")
    return (
      <>
        Waiting for this message. It appears once the sender or one of your
        other devices is online.
      </>
    );
  // The web keeps the last 30 days; the phone app has the whole history.
  if (!recent && !isPhone(nativePlatform))
    return (
      <>
        This message is older than {HISTORY_DAYS} days. Open xmm on your phone
        to read it.
      </>
    );
  return (
    <>
      Waiting for this message.{" "}
      <button
        class="link"
        onClick={() =>
          void engine.value
            ?.requestHistoryAgain()
            .then(() => toast("Asked your other devices for your chat history"))
        }
      >
        Get chat history
      </button>
    </>
  );
}
